import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { fetchAdminStatus, type AdminStatusResult } from '../apiClient';

/** `checking` covers both "no answer yet" and "no token to ask with". */
export type AdminStatus = 'checking' | 'admin' | 'not-admin' | 'error';

/**
 * The caller's administrator status (A4a).
 *
 * One place asks, so the guard and the navigation cannot disagree — a link that
 * appears for someone the guard then refuses is worse than no link at all.
 *
 * **The answer is derived, never stored.** It lives in component state for the
 * lifetime of a mount and is re-asked when the token changes, which includes
 * signing out and signing in as someone else. Caching it in `localStorage` or a
 * module-level variable would let a stale `admin` survive a sign-out; the check
 * is one request and correctness is worth more than saving it.
 *
 * **Fails closed at every step.** No token, an error, or an outright refusal all
 * leave the caller without access. Only a 200 grants it, and the server has
 * already made that decision — this hook reports it and never infers it.
 *
 * A `password-recovery` session is excluded for the reason `RequireAuth` states:
 * a valid token from an emailed link is not proof the holder knew the password,
 * and administrative access is the last thing that should accept it.
 */
export function useAdminStatus(): AdminStatus {
  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<AdminStatus>('checking');

  useEffect(() => {
    if (!token) {
      setState('checking');
      return;
    }
    let cancelled = false;
    setState('checking');
    void (async () => {
      const res: AdminStatusResult = await fetchAdminStatus(token);
      if (cancelled) return;
      setState(res.state === 'admin' ? 'admin' : res.state === 'not-admin' ? 'not-admin' : 'error');
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return state;
}
